import { useState, useEffect } from 'react';
import { BaseModal, BaseModalProps } from '../../../components/modals/BaseModal';
import { useModal } from '../../../contexts/ModalContext';
import { ExtraBilling } from '../../../models/ExtraBilling';
import { CardLayout } from '../../../components/layouts/CardLayout';
import { InputsLayout } from '../../../components/layouts/InputsLayout';
import { DatePicker } from '../../../components/common/DatePicker';
import { TextField } from '../../../components/common/TextField';
import { Dropdown } from '../../../components/common/Dropdown';
import { RadioButtonGroup } from '../../../components/common/RadioButtonGroup';
import { Box, InputAdornment } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { theme } from '../../../configs/theme';
import ThemeButton from '../../../components/common/ThemeButton';
import { DebitType } from '../../../types/DebitType';
import { SuffixTextField } from '../../../components/common/SuffixTextField';
import { TextWrapper } from '../../../components/common/TextWrapper';
import { formatDebitTypeUnit } from '../../../utils/formatDebitType';
import { DeleteConfirmationDialog } from '../../../components/common/DeleteConfirmationDialog';
import dayjs from 'dayjs';
import { Order } from '../../../models/Order';
import { Interval } from '../../../models/Interval';
import { HouseWorkType } from '../../../enums/HouseWorkType';
import { formatIntervalLabel } from '../../../utils/formatIntervalLabel';

const initialExtraBillingState: ExtraBilling = {
  startDate: '',
  endDate: '',
  description: '',
  priceType: '',
  amount: '',
  orders: [] as Order[],
  intervals: [] as Interval[],
  houseWorkType: null,
  isRut: false,
};

export default function ExtraBillingModal({ name }: BaseModalProps) {
  const { modalState, close } = useModal();
  const modalData = modalState[name] || { open: false, payload: {} };
  const mode = modalData.payload?.mode ?? 'create';
  const entity = modalData.payload?.project ?? modalData.payload?.contract;
  const existingExtraBilling = entity.extraBillings[0] as ExtraBilling;
  const onSave = modalData.payload?.onSave;
  const onDelete = modalData.payload?.onDelete;

  const [extraBilling, setExtraBilling] = useState<ExtraBilling>(initialExtraBillingState);
  const [errors, setErrors] = useState<Partial<Record<keyof ExtraBilling, string>>>({});
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedOrderValue, setSelectedOrderValue] = useState<string>('');
  const [selectedIntervalValue, setSelectedIntervalValue] = useState<string>('');

  useEffect(() => {
    if (modalData.open) {
      if (mode === 'edit' && existingExtraBilling) {
        const existingInterval = existingExtraBilling.intervals?.[0];
        setExtraBilling(existingExtraBilling);
        // Older billings were saved on every order, so the interval tells which order is theirs.
        setSelectedOrderValue(
          existingInterval?.orderId ?? existingExtraBilling.orders?.[0]?.id ?? ''
        );
        setSelectedIntervalValue(existingInterval?.id ?? '');
      } else {
        // On a RUT project or contract, most extra billings are RUT work, so new ones start as RUT.
        setExtraBilling({ ...initialExtraBillingState, isRut: !!entity?.rut });
        setSelectedOrderValue('');
        setSelectedIntervalValue('');
      }
      setErrors({});
    }
  }, [modalData.open, mode, existingExtraBilling, entity?.rut]);

  const orders: Order[] = entity.orders ?? [];
  // Only the chosen order's intervals can be picked, so the billing lands on that order's invoice.
  const selectedOrder = orders.find((order) => order.id === selectedOrderValue);
  const intervals: Interval[] = selectedOrder?.intervals ?? [];
  const selectedInterval = intervals.find((interval) => interval.id === selectedIntervalValue);
  const needsHouseWorkType = entity.rut && extraBilling.isRut;

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setExtraBilling((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name as keyof ExtraBilling]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // The type is kept when switching to Nej, so switching back restores it; the backend drops it
  // from a non-RUT billing on save.
  const handleRutChange = (isRut: boolean) => {
    setExtraBilling((prev) => ({ ...prev, isRut }));
    setErrors((prev) => ({ ...prev, houseWorkType: undefined }));
  };

  const handleOrderChange = (e: SelectChangeEvent<string>) => {
    setSelectedOrderValue(e.target.value);
    setSelectedIntervalValue('');
    setErrors((prev) => ({ ...prev, orders: undefined }));
  };

  const handleIntervalChange = (e: SelectChangeEvent<string>) => {
    setSelectedIntervalValue(e.target.value);
    setErrors((prev) => ({ ...prev, intervals: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ExtraBilling, string>> = {};

    if (!extraBilling.startDate) {
      newErrors.startDate = 'Startdatum är obligatoriskt';
    }
    if (!extraBilling.description) {
      newErrors.description = 'Beskrivning är obligatorisk';
    }
    if (!extraBilling.priceType) {
      newErrors.priceType = 'Prissättningsform är obligatorisk';
    }
    if (!extraBilling.amount) {
      newErrors.amount = 'Belopp är obligatorisk';
    }
    if (!selectedOrder) {
      newErrors.orders = 'Order är obligatorisk';
    }
    if (!selectedInterval) {
      newErrors.intervals = 'Intervall är obligatoriskt';
    }
    if (needsHouseWorkType && !extraBilling.houseWorkType) {
      newErrors.houseWorkType = 'Hushållsarbete är obligatoriskt';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    if (onSave) {
      onSave({
        ...extraBilling,
        orders: selectedOrder ? [selectedOrder] : [],
        intervals: selectedInterval ? [selectedInterval] : [],
      });
    }
    close(name);
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(false);
    if (onDelete) {
      onDelete(extraBilling);
      close(name);
    }
  };

  return (
    <BaseModal
      name={name}
      title={mode === 'edit' ? 'Redigera extradebitering' : 'Lägg till extradebitering'}
      size="medium"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <CardLayout>
            <InputsLayout fieldsinarow={1}>
              <TextField
                name="description"
                required
                value={extraBilling.description}
                onChange={handleChange}
                error={!!errors.description}
                errorText={errors.description}
                wrapperProps={{
                  label: 'Beskrivning',
                }}
              />
            </InputsLayout>
            <InputsLayout>
              <DatePicker
                name="startDate"
                required
                value={extraBilling.startDate}
                onChange={handleChange}
                error={!!errors.startDate}
                errorText={errors.startDate}
                wrapperProps={{
                  label: 'Startdatum',
                }}
              />
              <DatePicker
                name="endDate"
                value={extraBilling.endDate}
                onChange={handleChange}
                minDate={extraBilling.startDate}
                errorText={errors.endDate}
                wrapperProps={{
                  label: 'Slutdatum',
                }}
              />
            </InputsLayout>
            <InputsLayout>
              <Dropdown
                name="order"
                value={selectedOrderValue}
                required
                onChange={handleOrderChange}
                error={!!errors.orders}
                errorText={errors.orders}
                data={orders.map((order) => ({
                  title: order.orderService?.service?.name || '',
                  value: order.id || '',
                }))}
                wrapperProps={{
                  label: 'Order',
                }}
              />
              <Dropdown
                name="interval"
                value={selectedIntervalValue}
                required
                onChange={handleIntervalChange}
                error={!!errors.intervals}
                errorText={errors.intervals}
                data={intervals.map((interval) => ({
                  title: formatIntervalLabel(interval),
                  value: interval.id || '',
                }))}
                wrapperProps={{
                  label: 'Intervall',
                }}
              />
            </InputsLayout>
            <InputsLayout>
              <Dropdown
                required
                name="priceType"
                value={extraBilling.priceType}
                onChange={handleChange}
                error={!!errors.priceType}
                errorText={errors.priceType}
                data={[
                  {
                    title: 'Månadspris',
                    value: DebitType.Monthly,
                  },
                  {
                    title: 'Veckopris',
                    value: DebitType.Weekly,
                  },
                  {
                    title: 'Engångspris',
                    value: DebitType.OneTime,
                  },
                  {
                    title: 'Löpande pris',
                    value: DebitType.Ongoing,
                  },
                  {
                    title: 'À-pris',
                    value: DebitType.Unit,
                  },
                ]}
                wrapperProps={{
                  label: 'Prissättningsform',
                }}
              />
              <TextWrapper label="Belopp" required={true}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <SuffixTextField
                    name="amount"
                    value={extraBilling.amount}
                    onFocus={() => setIsAmountFocused(true)}
                    onBlur={() => {
                      if (!extraBilling.amount) {
                        setIsAmountFocused(false);
                      }
                    }}
                    onChange={handleChange}
                    slotProps={{
                      input: {
                        endAdornment: (isAmountFocused || extraBilling.amount) &&
                          extraBilling.priceType && (
                            <InputAdornment sx={{ paddingRight: '6px' }} position="end">
                              kr {formatDebitTypeUnit(extraBilling.priceType)}
                            </InputAdornment>
                          ),
                      },
                    }}
                  />
                  {errors.amount && (
                    <p
                      style={{
                        color: theme.palette.status.rejected,
                        fontSize: '12px',
                        marginTop: 6,
                      }}
                    >
                      {errors.amount}
                    </p>
                  )}
                </Box>
              </TextWrapper>
            </InputsLayout>
            {entity.rut && (
              <InputsLayout>
                <RadioButtonGroup
                  wrapperProps={{ label: 'Rut-avdrag' }}
                  groupValue={String(extraBilling.isRut)}
                  onChange={(e) => handleRutChange(e.target.value === 'true')}
                  name="isRut"
                  required
                  radioButtons={[
                    { title: 'Ja', value: 'true' },
                    { title: 'Nej', value: 'false' },
                  ]}
                />
              </InputsLayout>
            )}
            <InputsLayout>
              {needsHouseWorkType && (
                <Dropdown
                  required
                  name="houseWorkType"
                  value={extraBilling.houseWorkType ?? ''}
                  onChange={handleChange}
                  error={!!errors.houseWorkType}
                  errorText={errors.houseWorkType}
                  data={[
                    {
                      title: 'Snöskottning',
                      value: HouseWorkType.Snowplowing,
                    },
                    {
                      title: 'Städning',
                      value: HouseWorkType.Cleaning,
                    },
                    {
                      title: 'Tvättning',
                      value: HouseWorkType.Washing,
                    },
                    {
                      title: 'Flytt',
                      value: HouseWorkType.Moving,
                    },
                    {
                      title: 'Trädgårdsarbete',
                      value: HouseWorkType.Gardening,
                    },
                  ]}
                  wrapperProps={{
                    label: 'Hushållsarbete',
                  }}
                />
              )}

              {entity.customer?.type === 'COMPANY' && (
                <Dropdown
                  name="vatType"
                  value={extraBilling.vatType ?? ''}
                  data={[
                    { title: 'SE', value: 'standard' },
                    { title: 'Omvänd', value: 'reverse' },
                  ]}
                  onChange={handleChange}
                  wrapperProps={{ label: 'Momstyp' }}
                />
              )}
            </InputsLayout>
          </CardLayout>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'space-between',
            padding: '0.5rem',
            backgroundColor: theme.palette.customColors.cultured,
          }}
        >
          {mode === 'edit' && onDelete && (
            <ThemeButton onClick={() => setShowDeleteConfirmation(true)} buttonType="error">
              Ta bort
            </ThemeButton>
          )}
          <Box sx={{ marginLeft: 'auto', display: 'flex', gap: '1rem' }}>
            <ThemeButton onClick={handleSubmit}>
              {mode === 'edit' ? 'Uppdatera' : 'Spara'}
            </ThemeButton>
          </Box>
        </Box>
      </Box>

      <DeleteConfirmationDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDelete}
        title="Ta bort extradebitering"
        message="Är du säker på att du vill ta bort denna extradebitering?"
      />
    </BaseModal>
  );
}
